// ============================================
// Kernel scan — discover compute kernels in user Slang source
// ============================================
//
// Declaration-level source scan (NOT body splicing — user code is never modified). Slang's wasm
// reflection exposes no decl-tree walk, so kernel discovery happens here and wrapperGen appends the
// `[shader("compute")]` entry points the reflection then reports. Two shapes are recognised:
//
//   struct Advect : IComputeKernel { ... [NumThreads(64)] void execute(uint3 id) { ... } }
//   [Kernel(8, 8)] void blur(uint2 id) { ... }
//
// Comments and string literals are blanked (line structure preserved) before matching, so `// struct
// Fake : IComputeKernel` can't false-positive. Thread-group sizes must be integer literals — the
// engine generates `[numthreads(x,y,z)]` from them and validates WebGPU base limits early.

export interface ScannedKernel {
  /** Struct name (model 'struct') or function name (model 'function'). */
  name: string;
  model: 'struct' | 'function';
  numThreads: [number, number, number];
  /** Dispatch-id parameter type; struct kernels are always 'uint3'. */
  idType: 'uint' | 'uint2' | 'uint3';
  /** 1-based line of the declaration (diagnostics remap target for wrapper errors). */
  declLine: number;
}

export interface KernelScanResult {
  kernels: ScannedKernel[];
  /** True when at least one kernel was found (⇒ the asset is a compute shader). */
  isCompute: boolean;
  errors: { message: string; line: number }[];
}

// WebGPU base limits (maxComputeWorkgroupSize X/Y/Z + maxComputeInvocationsPerWorkgroup).
const MAX_WG = [256, 256, 64] as const;
const MAX_INVOCATIONS = 256;

/** Blank comments and string literals with spaces, preserving newlines (indices/lines stay valid). */
function blankCommentsAndStrings(source: string): string {
  const out = source.split('');
  const n = source.length;
  let i = 0;
  const blank = (from: number, to: number): void => {
    for (let k = from; k < to; k++) if (out[k] !== '\n') out[k] = ' ';
  };
  while (i < n) {
    const c = source[i];
    const next = source[i + 1];
    if (c === '/' && next === '/') {
      const end = source.indexOf('\n', i);
      const stop = end === -1 ? n : end;
      blank(i, stop);
      i = stop;
    } else if (c === '/' && next === '*') {
      const end = source.indexOf('*/', i + 2);
      const stop = end === -1 ? n : end + 2;
      blank(i, stop);
      i = stop;
    } else if (c === '"') {
      let j = i + 1;
      while (j < n && source[j] !== '"' && source[j] !== '\n') {
        if (source[j] === '\\') j++;
        j++;
      }
      const stop = Math.min(j + 1, n);
      blank(i, stop);
      i = stop;
    } else {
      i++;
    }
  }
  return out.join('');
}

/** 1-based line of a character index. */
function lineOf(source: string, index: number): number {
  let line = 1;
  for (let i = 0; i < index && i < source.length; i++) if (source[i] === '\n') line++;
  return line;
}

/** Index of the `}` matching the `{` at openIndex, or -1. */
function matchBrace(source: string, openIndex: number): number {
  let depth = 0;
  for (let i = openIndex; i < source.length; i++) {
    if (source[i] === '{') depth++;
    else if (source[i] === '}') {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
}

/** Parse `[NumThreads(...)]` / `[Kernel(...)]` args: 1–3 positive int literals (y/z default 1). */
function parseThreadArgs(
  raw: string,
  attrName: string,
  line: number,
  errors: KernelScanResult['errors'],
): [number, number, number] | null {
  const parts = raw.split(',').map((s) => s.trim()).filter((s) => s.length > 0);
  if (parts.length < 1 || parts.length > 3) {
    errors.push({ message: `[${attrName}] expects 1–3 arguments, got ${parts.length}`, line });
    return null;
  }
  const dims: number[] = [1, 1, 1];
  for (let i = 0; i < parts.length; i++) {
    if (!/^\d+$/.test(parts[i])) {
      errors.push({
        message: `[${attrName}] argument ${i + 1} must be an integer literal (got '${parts[i]}') — thread-group sizes cannot be expressions`,
        line,
      });
      return null;
    }
    dims[i] = Number(parts[i]);
  }
  const [x, y, z] = dims;
  if (x < 1 || y < 1 || z < 1) {
    errors.push({ message: `[${attrName}] sizes must be ≥ 1 (got ${x}, ${y}, ${z})`, line });
    return null;
  }
  if (x > MAX_WG[0] || y > MAX_WG[1] || z > MAX_WG[2] || x * y * z > MAX_INVOCATIONS) {
    errors.push({
      message: `[${attrName}(${x}, ${y}, ${z})] exceeds WebGPU base limits (x,y ≤ 256, z ≤ 64, x·y·z ≤ 256)`,
      line,
    });
    return null;
  }
  return [x, y, z];
}

/**
 * Scan Slang source for compute kernel declarations. Never throws; problems land in `errors`
 * (importer surfaces them as diagnostics). A file with zero kernels and zero errors is simply
 * not a compute shader.
 */
export function scanKernels(source: string): KernelScanResult {
  const errors: KernelScanResult['errors'] = [];
  const kernels: ScannedKernel[] = [];
  const src = blankCommentsAndStrings(source);

  // ---- Model A: struct <Name> : ... IComputeKernel ... { body } ----
  const structRe = /\bstruct\s+([A-Za-z_]\w*)\s*:\s*([^{;]+)\{/g;
  let m: RegExpExecArray | null;
  while ((m = structRe.exec(src)) !== null) {
    const [, name, bases] = m;
    if (!/\bIComputeKernel\b/.test(bases)) continue;
    const declLine = lineOf(src, m.index);
    const openIdx = m.index + m[0].length - 1;
    const closeIdx = matchBrace(src, openIdx);
    if (closeIdx === -1) {
      errors.push({ message: `struct ${name}: unbalanced braces`, line: declLine });
      continue;
    }
    const body = src.slice(openIdx + 1, closeIdx);

    const exec = /\bvoid\s+execute\s*\(\s*uint3\s+[A-Za-z_]\w*\s*\)/.exec(body);
    if (!exec) {
      errors.push({
        message: `struct ${name} : IComputeKernel must declare 'void execute(uint3 id)'`,
        line: declLine,
      });
      continue;
    }
    const nt = /\[\s*NumThreads\s*\(([^)]*)\)\s*\]/.exec(body);
    if (!nt) {
      errors.push({
        message: `struct ${name}: 'execute' needs a [NumThreads(x, y, z)] attribute (y/z optional)`,
        line: declLine,
      });
      continue;
    }
    const numThreads = parseThreadArgs(nt[1], 'NumThreads', lineOf(src, openIdx + 1 + (nt.index ?? 0)), errors);
    if (!numThreads) continue;
    kernels.push({ name, model: 'struct', numThreads, idType: 'uint3', declLine });
  }

  // ---- Model B: [Kernel(...)] (other attrs allowed between) void <name>(uintN id) ----
  const kernelAttrRe = /\[\s*Kernel\s*\(([^)]*)\)\s*\]/g;
  while ((m = kernelAttrRe.exec(src)) !== null) {
    const attrLine = lineOf(src, m.index);
    const after = src.slice(m.index + m[0].length);
    const fn = /^\s*(?:\[[^\]]*\]\s*)*(?:public\s+)?(\w[\w<>0-9]*)\s+([A-Za-z_]\w*)\s*\(\s*([A-Za-z_]\w*)\s+[A-Za-z_]\w*\s*\)/.exec(after);
    if (!fn) {
      errors.push({
        message: `[Kernel] must be followed by 'void <name>(uint|uint2|uint3 id)'`,
        line: attrLine,
      });
      continue;
    }
    const [, retType, name, idType] = fn;
    if (retType !== 'void') {
      errors.push({ message: `[Kernel] function '${name}' must return void (got ${retType})`, line: attrLine });
      continue;
    }
    if (idType !== 'uint' && idType !== 'uint2' && idType !== 'uint3') {
      errors.push({
        message: `[Kernel] function '${name}' must take a single uint/uint2/uint3 dispatch id (got ${idType})`,
        line: attrLine,
      });
      continue;
    }
    const numThreads = parseThreadArgs(m[1], 'Kernel', attrLine, errors);
    if (!numThreads) continue;
    kernels.push({ name, model: 'function', numThreads, idType, declLine: attrLine });
  }

  // ---- Cross-checks ----
  const seen = new Map<string, ScannedKernel>();
  for (const k of kernels) {
    const dup = seen.get(k.name);
    if (dup) {
      errors.push({
        message: `duplicate kernel name '${k.name}' (${dup.model} at line ${dup.declLine}, ${k.model} at line ${k.declLine})`,
        line: k.declLine,
      });
    } else {
      seen.set(k.name, k);
    }
  }
  const unique = [...seen.values()].sort((a, b) => a.declLine - b.declLine);

  return { kernels: unique, isCompute: unique.length > 0, errors };
}
